import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { LocaleContext, useLocalizeMessage, useStudyString } from 'libs.nucleus';
import { PageHeader, PageWrapper } from 'libs.react';
import { SendNotificationModal } from './components/send_notification_modal.component';
import { hasDeliverableParticipantEmail, hasDeliverableParticipantMobile, } from './notification_channel_delivery.helpers';
import { ParticipantEventTimeline } from './participant_event_timeline.component';
import { getApiInstance } from '../../helpers/api.helpers';
import { getRouteLink } from '../../helpers/links.helpers';
import { getParticipantNumber } from '../../helpers/participants.helpers';
import { isEventTimelineEnabled } from '../../helpers/timeline.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
export const ParticipantEventTimelinePage = () => {
    const translate = useLocalizeMessage();
    const { environment } = useCurrentEnvironment();
    const org = useOrgContext();
    const site = useSiteContext();
    const currentLocale = useContext(LocaleContext);
    const { isUserPermitted } = useLumosAuth();
    const match = useMatch('/site/study/:studyId/participants/:participantId/timeline');
    const participantId = match?.params.participantId;
    const [isSendNotificationOpen, setIsSendNotificationOpen] = useState(false);
    // Page-local fetch: shared `getParticipantDetails` swallows errors (returns `{}`), which
    // would make React Query report success and the modal mis-label fetch failures as "no account".
    const { data: participantData, isSuccess: isParticipantDetailsReady, isError: isParticipantDetailsError, } = useQuery({
        queryKey: ['participantDetails', participantId, currentLocale, 'timeline'],
        queryFn: async () => {
            const response = await getApiInstance(environment, currentLocale).get(`/v2/routes/site_subjects/${site?._id ?? ''}`, {
                params: {
                    expand: ['c_visit_schedule', 'c_caregiver_relationship.c_caregivers_info.c_public_user'],
                    paths: ['c_set_dates', 'c_email', 'c_mobile', 'c_account'],
                    where: JSON.stringify({ _id: participantId }),
                },
            });
            return response?.data?.data[0] || {};
        },
        enabled: !!environment && !!participantId && !!site?._id,
    });
    const participantNumber = useStudyString(participantData ? getParticipantNumber(participantData) : '');
    // Mirror the entry-point gate in participant_details.page.tsx so a direct navigation to
    // /timeline is also blocked below the Axon version that ships the backing route
    // (c_list_public_user_notifications, AXONCONFIG-5248).
    if (!isEventTimelineEnabled(environment, org) || !environment || !participantId) {
        return null;
    }
    const headerOptions = [];
    // No send-specific Lumos permission yet. PARTICIPANTS_CREATE maps to the same write roles
    // BE allows for send (Site User / Site Investigator) — same gate as "Add participant".
    // Switch to a dedicated permission if those role sets diverge.
    if (isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_CREATE)) {
        headerOptions.push({
            name: translate('action.timeline.send_notification'),
            onClick: () => setIsSendNotificationOpen(true),
            variant: 'secondary',
            dataTestId: 'send-notification-button',
        });
    }
    return (_jsx(PageWrapper, { children: _jsxs("div", { "data-testid": 'timeline-page', children: [_jsx(PageHeader, { breadcrumbPages: [
                        { name: translate('generic.participants'), path: getRouteLink('participants', environment) },
                        {
                            name: participantData?.c_number ? participantNumber : translate('label.participant_details.detail'),
                            path: `${getRouteLink('participants', environment)}/${participantId}`,
                        },
                        { name: translate('label.timeline.title_page'), path: '' },
                    ], title: translate('label.timeline.title_page'), options: headerOptions, LinkComponent: Link }), _jsx(ParticipantEventTimeline, { participantId: participantId, siteId: site?._id ?? '' }), _jsx(SendNotificationModal, { isOpen: isSendNotificationOpen, onClose: () => setIsSendNotificationOpen(false), participantId: participantId, siteId: site?._id ?? '', hasParticipantEmail: isParticipantDetailsReady && hasDeliverableParticipantEmail(participantData?.c_email), hasParticipantMobile: isParticipantDetailsReady && hasDeliverableParticipantMobile(participantData?.c_mobile), hasParticipantAccount: isParticipantDetailsReady && !!participantData?.c_account, isParticipantDetailsReady: isParticipantDetailsReady, isParticipantDetailsError: isParticipantDetailsError })] }) }));
};
